<ion-content class="ion-padding bg-white dark:bg-stone-900">
    <!-- Questionnaire Modal -->
    <div *ngIf="showQuestionnaireModal && currentQuestion" class="hero-bleed fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
        <div class="glass bg-stone-900 rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4 border border-stone-800">
            <div class="mb-4">
                <span class="text-sm text-stone-300">{{ 'questionnaire.question_label' | t }} {{ currentQuestionIndex }} {{ 'questionnaire.of' | t }} {{ questionnaire?.user_profile_questions?.length }}</span>
                <div class="w-full rounded-full h-2 mt-2 progress-bar-bg">
                    <div class="h-2 rounded-full transition-all progress-bar-fill" [style.width.%]="(currentQuestionIndex / questionnaire?.user_profile_questions?.length!) * 100"></div>
                </div>
            </div>

            <h2 class="text-xl font-bold text-white mb-6">{{ getTranslatedQuestion(currentQuestion) }}</h2>

            <!-- Single Choice Buttons -->
            <div *ngIf="currentQuestion.type === 'single_choice'" class="space-y-3">
                <button
                    *ngFor="let option of currentQuestion.options; let i = index"
                    (click)="answerQuestion(option)"
                    class="w-full px-4 py-3 text-left rounded-lg border border-stone-800 hover:border-stone-700 hover:bg-black/30 text-white transition-all">
                    {{ getTranslatedOption(currentQuestion, i) }}
                </button>
            </div>

            <!-- Multi Choice Checkboxes -->
            <div *ngIf="currentQuestion.type === 'multi_choice'" class="space-y-3">
                <div class="multi-choice-container space-y-3">
                    <label *ngFor="let option of currentQuestion.options; let i = index" class="flex items-center gap-3 p-3 rounded-lg border border-stone-800 hover:border-stone-700 hover:bg-black/30 cursor-pointer transition-all">
                        <input
                            type="checkbox"
                            [checked]="isOptionSelected(option)"
                            (change)="toggleMultiChoiceOption(option)"
                            class="w-4 h-4" />
                        <span class="text-white">{{ getTranslatedOption(currentQuestion, i) }}</span>
                    </label>
                </div>
                <button
                    *ngIf="selectedAnswers && selectedAnswers.length > 0"
                    (click)="answerQuestion(selectedAnswers)"
                    class="w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors mt-4 questionnaire-btn">
                    {{ 'questionnaire.continue' | t }}
                </button>
            </div>

            <!-- Scale Slider -->
            <div *ngIf="currentQuestion.type === 'scale'" class="space-y-6">
                <div class="flex justify-between text-sm text-stone-300 mb-2">
                    <span>{{ getScaleDescriptionMin() }}</span>
                    <span>{{ getScaleDescriptionMax() }}</span>
                </div>
                <div class="flex items-center gap-4">
                    <input
                        #scaleInput
                        type="range"
                        [min]="currentQuestion.min || 1"
                        [max]="currentQuestion.max || 5"
                        [value]="selectedAnswers"
                        (input)="onScaleSliderChange(scaleInput.value)"
                        class="flex-1 h-2 progress-bar-bg rounded-lg appearance-none cursor-pointer" />
                    <div class="text-2xl font-bold text-stone-300 min-w-12 text-center">{{ selectedAnswers }}</div>
                </div>
                <button
                    (click)="answerQuestion(selectedAnswers)"
                    class="w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors mt-4 questionnaire-btn">
                    {{ 'questionnaire.continue' | t }}
                </button>
            </div>
        </div>
    </div>

    <!-- Password Protection Modal -->
    <div *ngIf="showPasswordModal" class="hero-bleed fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
        <div class="glass bg-stone-900 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-stone-800">
            <h2 class="text-2xl font-bold text-white mb-2">{{ 'auth.title' | t }}</h2>
            <p class="text-stone-300 mb-6">{{ 'auth.description' | t }}</p>
            
            <div class="space-y-4">
                <input
                    type="text"
                    [(ngModel)]="usernameInput"
                    (keypress)="onCredentialKeyPress($event)"
                    [placeholder]="'auth.username' | t"
                    class="w-full px-4 py-3 rounded-lg bg-white text-stone-900 border border-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                    autocomplete="off"
                />

                <input
                    type="password"
                    [(ngModel)]="passwordInput"
                    (keypress)="onCredentialKeyPress($event)"
                    [placeholder]="'auth.password' | t"
                    class="w-full px-4 py-3 rounded-lg bg-white text-stone-900 border border-stone-200 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                    autocomplete="off"
                />
                
                <div *ngIf="passwordError" class="text-sm text-red-400 font-medium">{{ passwordError }}</div>
                
                <button
                    (click)="verifyCredentials()"
                    class="w-full px-4 py-3 questionnaire-btn text-white font-semibold rounded-lg transition-colors mt-2">
                    {{ 'auth.login' | t }}
                </button>
            </div>
        </div>
    </div>

    <!-- Main Content (hidden until authenticated, but still in DOM for avatar initialization) -->
    <div [hidden]="showPasswordModal || showQuestionnaireModal">
        <div class="container">
            <!-- Rich Hero -->
            <section class="hero-bleed relative overflow-hidden my-0">
                <div
                    class="absolute -left-40 -top-24 opacity-30 blur-3xl transform rotate-12 w-96 h-96 bg-gradient-to-r from-primary-400 to-purple-400 rounded-full">
                </div>
                <div
                    class="absolute -right-32 -bottom-20 opacity-20 blur-2xl w-80 h-80 bg-gradient-to-r from-amber-300 to-pink-300 rounded-full">
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center relative z-10">
                    <!-- Left: personal intro -->
                    <div class="space-y-6 flex flex-col">
                        <div class="glass">
                            <p class="text-lg text-stone-200 mt-4 text-justify">{{ 'demo.description' | t }}</p>

                            <!-- Prominent bot reply bubble (main hero) -->
                            <div *ngIf="lastBotMessage && !isAwaitingResponse" class="mt-6">
                                <div class="rounded-xl bg-black/60 dark:bg-black/60 text-white p-6 shadow-lg">
                                    <p class="text-lg leading-relaxed bot-message-bubble" style="overflow: auto; height: 240px; font-size:12px;">{{ lastBotMessage }}</p>
                                </div>
                            </div>
                            <!-- Thinking spinner (visible while awaiting response) -->
                            <div *ngIf="isAwaitingResponse" class="mt-6">
                                <div class="rounded-xl bg-black/60 dark:bg-black/60 text-white p-6 shadow-lg">
                                    <div class="flex gap-2 items-center">
                                        <span class="w-3 h-3 bg-white/80 rounded-full animate-bounce" style="animation-delay:0ms"></span>
                                        <span class="w-3 h-3 bg-white/80 rounded-full animate-bounce" style="animation-delay:160ms"></span>
                                        <span class="w-3 h-3 bg-white/80 rounded-full animate-bounce" style="animation-delay:320ms"></span>
                                    </div>
                                    <div class="mt-2 text-sm text-stone-100">
                                        {{ generationStatusText || ('Avatar: ' + avatarMotionState.toUpperCase()) }}
                                    </div>
                                </div>
                            </div>
                            <div class="flex flex-wrap gap-3 items-center mt-6">
                                <div class="w-full md">
                                    <div *ngIf="isStartupLoading" class="rounded-xl bg-black/45 text-white p-5 shadow-lg border border-white/15">
                                        <div class="flex items-center gap-3">
                                            <span class="w-3 h-3 bg-white/80 rounded-full animate-bounce" style="animation-delay:0ms"></span>
                                            <span class="w-3 h-3 bg-white/80 rounded-full animate-bounce" style="animation-delay:140ms"></span>
                                            <span class="w-3 h-3 bg-white/80 rounded-full animate-bounce" style="animation-delay:280ms"></span>
                                            <span class="text-sm md:text-base">{{ startupStatusText }}</span>
                                        </div>
                                        <div class="mt-3 text-xs text-stone-200/90">
                                            <div>Modello in-browser: <strong>{{ inBrowserLoading ? 'caricamento...' : 'pronto' }}</strong></div>
                                            <div>Avatar 3D: <strong>{{ avatarLoading ? 'caricamento...' : 'pronto' }}</strong></div>
                                        </div>
                                        <div *ngIf="inBrowserError" class="text-sm text-red-300 mt-2">Errore modello: {{ inBrowserError }}</div>
                                        <div *ngIf="avatarError" class="text-sm text-red-300 mt-1">Errore avatar: {{ avatarError }}</div>
                                    </div>

                                    <div *ngIf="isAssistantReady">
                                    <form class="prompt-shell" (submit)="$event.preventDefault(); onSend(mainInput.value); mainInput.value='';">
                                        <div class="prompt-head">{{ 'home.writeHere' | t }}</div>
                                        <div class="prompt-row">
                                            <input
                                                #mainInput
                                                type="text"
                                                placeholder=""
                                                [disabled]="isAwaitingResponse"
                                                class="prompt-input"
                                                aria-label="Messaggio" />

                                            <button
                                                type="button"
                                                (click)="toggleListening('main')"
                                                [disabled]="isAwaitingResponse"
                                                [class.is-active]="isListening && currentListeningTarget==='main'"
                                                [class.opacity-60]="!supportsSpeechRecognition"
                                                class="icon-btn"
                                                aria-label="Attiva microfono">
                                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M12 3a3 3 0 0 0-3 3v5a3 3 0 1 0 6 0V6a3 3 0 0 0-3-3z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                                    <path d="M5 11a7 7 0 0 0 14 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                                    <path d="M12 18v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                                </svg>
                                            </button>

                                            <button
                                                type="submit"
                                                [disabled]="isAwaitingResponse"
                                                class="send-btn"
                                                aria-label="Invia messaggio">
                                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M4 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                                    <path d="M13 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                        <div class="prompt-underline"></div>
                                    </form>

                                    <div class="suggestions-wrap" aria-label="Suggerimenti">
                                        <button
                                            *ngFor="let suggestion of quickSuggestions"
                                            type="button"
                                            class="suggestion-chip"
                                            [disabled]="isAwaitingResponse"
                                            (click)="onSuggestionClick(suggestion, mainInput)">
                                            {{ suggestion }}
                                        </button>
                                    </div>

                                    <div class="inline-block align-middle mt-3">
                                        <div class="w-20 h-2 bg-white/10 rounded overflow-hidden">
                                            <div class="h-2 bg-green-400" [style.width.%]="micLevel * 100"></div>
                                        </div>
                                        <div *ngIf="currentListeningTarget==='main' && listeningHint" class="text-xs text-yellow-300">{{ listeningHint }}</div>
                                    </div>

                                    <div class="mt-2">
                                        <span class="text-sm text-stone-200">
                                            Modello in-browser: pronto
                                        </span>
                                        <span class="text-sm text-stone-200 ml-3">
                                            Avatar: {{ avatarMotionState.toUpperCase() }}
                                        </span>
                                        <div *ngIf="inBrowserError" class="text-sm text-red-300 mt-1">Errore: {{ inBrowserError }}</div>
                                        <div *ngIf="avatarError" class="text-sm text-red-300 mt-1">Errore avatar: {{ avatarError }}</div>
                                    </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right: 3D character (kept for small screens) -->
                    <div class="flex justify-center md:justify-end">
                        <div class="relative w-72 md:w-80 lg:w-96 flex flex-col items-center model-wrap">
                            <div class="w-64 h-64 model-bg rounded-lg shadow-lg mobile-model"></div>
                            <div class="mt-3 w-full" *ngIf="isAssistantReady">
                                <form class="flex gap-2 items-center"
                                    (submit)="$event.preventDefault(); onSend(messageInput.value); messageInput.value='';">
                                    <button type="button" (click)="toggleListening('message')"
                                        [disabled]="isAwaitingResponse"
                                        [class.opacity-60]="!supportsSpeechRecognition"
                                        [class.bg-red-600]="isListening && currentListeningTarget==='message'"
                                        class="px-3 py-2 rounded-md mr-2 bg-primary-700 text-white">🎤</button>
                                    <div class="inline-block align-middle ml-2">
                                        <div class="w-20 h-2 bg-white/10 rounded overflow-hidden">
                                            <div class="h-2 bg-green-400" [style.width.%]="micLevel * 100"></div>
                                        </div>
                                        <div *ngIf="currentListeningTarget==='message' && listeningHint"
                                            class="text-xs text-yellow-300">{{ listeningHint }}</div>
                                    </div>
                                    <input #messageInput type="text"
                                        placeholder="{{ 'home.writeHere' | t }}"
                                        [disabled]="isAwaitingResponse"
                                        class="flex-1 px-3 py-2 rounded-md bg-white/80 dark:bg-white/10 text-stone-800 dark:text-white placeholder:text-stone-500 dark:placeholder:text-stone-400" />
                                    <ion-button type="submit" color="primary" fill="outline" size="large" [disabled]="isAwaitingResponse">
                                        {{ isAwaitingResponse ? 'Attendi...' : 'Invia' }}
                                    </ion-button>
                                </form>
                                <div class="mt-2 space-y-2">
                                    <div *ngFor="let m of messages">
                                        <div *ngIf="m.from==='user'"
                                            class="inline-block px-3 py-2 rounded-lg bg-white/80 text-stone-800 text-sm">Tu:
                                            {{ m.text }}</div>
                                        <div *ngIf="m.from==='bot' && m.text"
                                            class="block w-full px-3 py-2 rounded-lg bg-white/90 text-stone-900 text-sm font-medium bot-message">
                                            Assistente: {{ m.text }}</div>
                                    </div>
                                    <div *ngIf="isAwaitingResponse" class="flex gap-1.5 items-center px-3 py-2">
                                        <span class="w-2 h-2 bg-white/70 rounded-full animate-bounce" style="animation-delay:0ms"></span>
                                        <span class="w-2 h-2 bg-white/70 rounded-full animate-bounce" style="animation-delay:160ms"></span>
                                        <span class="w-2 h-2 bg-white/70 rounded-full animate-bounce" style="animation-delay:320ms"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Fixed positioned model: bottom-left on large screens | Hidden until questionnaire completed -->
                <div class="model-fixed" [hidden]="!showAvatar">
                    <div #modelContainer class="model-bg fixed-canvas rounded-lg shadow-lg"></div>
                    <div class="model-controls" *ngIf="isAssistantReady">
                        <!-- Animation cycle buttons -->
                        <div class="flex gap-1 mb-2 justify-center">
                            <button type="button" (click)="cycleIdleClip(-1)" 
                                title="Previous idle animation"
                                class="px-2 py-1 text-sm bg-stone-700 hover:bg-stone-600 text-white rounded">
                                ◀ Prev Idle
                            </button>
                            <button type="button" (click)="cycleIdleClip(1)" 
                                title="Next idle animation"
                                class="px-2 py-1 text-sm bg-stone-700 hover:bg-stone-600 text-white rounded">
                                Next Idle ▶
                            </button>
                        </div>
                        <form class="flex gap-2 items-center"
                            (submit)="$event.preventDefault(); onSend(sideInput.value); sideInput.value='';">
                            <button type="button" (click)="toggleListening('side')"
                                [disabled]="isAwaitingResponse"
                                [class.opacity-60]="!supportsSpeechRecognition"
                                [class.bg-red-600]="isListening && currentListeningTarget==='side'"
                                class="px-3 py-1 rounded-md mr-2 bg-primary-700 text-white">🎤</button>
                            <div class="inline-block align-middle ml-2">
                                <div class="w-20 h-2 bg-white/10 rounded overflow-hidden">
                                    <div class="h-2 bg-green-400" [style.width.%]="micLevel * 100"></div>
                                </div>
                                <div *ngIf="currentListeningTarget==='side' && listeningHint"
                                    class="text-xs text-yellow-300">{{ listeningHint }}</div>
                            </div>
                            <input #sideInput type="text" placeholder="{{ 'home.writeHere' | t }}"
                                [disabled]="isAwaitingResponse"
                                class="px-3 py-2 rounded-md bg-white/80 dark:bg-white/10 text-stone-800 dark:text-white placeholder:text-stone-500 dark:placeholder:text-stone-400" />
                            <ion-button type="submit" color="primary" fill="outline" size="small" [disabled]="isAwaitingResponse">
                                {{ isAwaitingResponse ? 'Attendi...' : 'Invia' }}
                            </ion-button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    </div>
</ion-content>